import {
  ConfigurationMapEdge,
  ConfigurationMapField,
} from '@rossum/api-client/internal';

/**
 * Field types whose value TxScript computes from other fields. Only cycles
 * passing through one of these are problematic: the field is evaluated from a
 * snapshot of its dependencies, so anything feeding back into them (directly
 * or through hooks) works with stale values, e.g. D1 → F1 → H1 → H2 → D1.
 * Loops through captured/data fields alone — most often a hook that reads and
 * writes the same field — are ordinary write-back patterns, not errors, and
 * flagging them would drown out the real problems.
 */
const COMPUTED_FIELD_TYPES = new Set<ConfigurationMapField['type']>([
  'formula',
  'reasoning',
  'lookup',
]);

export const isComputedFieldType = (
  type: ConfigurationMapField['type']
): boolean => COMPUTED_FIELD_TYPES.has(type);

export type CycleResult = {
  /** Nodes lying on a problematic cycle. */
  cycleNodeIds: Set<string>;
  /** `${source}-${target}` keys of edges lying on a problematic cycle. */
  cycleEdgeKeys: Set<string>;
  /** Number of cycle clusters — overlapping loops count as one. */
  cycleCount: number;
};

/**
 * Drop edges that provably cannot lie on a cycle — one can only close a cycle
 * if its source has an incoming edge and its target an outgoing one — and
 * repeat until nothing more falls away. Every cycle survives untouched, so
 * this narrows the graph without changing any answer below. It is a superset
 * of the cycle edges (a bridge between two separate cycles survives), which is
 * why the exact grouping still follows; the point is that an acyclic graph
 * collapses to nothing and the expensive step below then has no work to do.
 */
const cyclicCore = (edges: ConfigurationMapEdge[]): ConfigurationMapEdge[] => {
  const sources = new Set(edges.map(edge => edge.source));
  const targets = new Set(edges.map(edge => edge.target));
  const kept = edges.filter(
    edge => targets.has(edge.source) && sources.has(edge.target)
  );

  return kept.length === edges.length ? kept : cyclicCore(kept);
};

/**
 * Nodes reachable from `start` by following one or more edges, walked one hop
 * at a time (same frontier shape as computeConnected). `start` itself appears
 * in the result only when it is reachable from itself — that is, when it lies
 * on a cycle.
 */
const reachableFrom = (
  edges: ConfigurationMapEdge[],
  start: string
): Set<string> => {
  const step = (reached: Set<string>, frontier: Set<string>): Set<string> => {
    const next = new Set(
      edges
        .filter(edge => frontier.has(edge.source) && !reached.has(edge.target))
        .map(edge => edge.target)
    );

    return next.size === 0
      ? reached
      : step(new Set([...reached, ...next]), next);
  };

  return step(new Set(), new Set([start]));
};

/**
 * Find the circular dependencies worth surfacing to the user: cycles in the
 * graph that pass through a computed field.
 */
export const computeCycles = (
  edges: ConfigurationMapEdge[],
  fields: ConfigurationMapField[]
): CycleResult => {
  const computedFieldIds = new Set(
    fields
      .filter(field => isComputedFieldType(field.type))
      .map(field => field.id)
  );

  const core = cyclicCore(edges);
  const nodeIds = Array.from(
    new Set(core.flatMap(edge => [edge.source, edge.target]))
  );
  const reachable = new Map(
    nodeIds.map(id => [id, reachableFrom(core, id)] as const)
  );
  const reaches = (from: string, to: string) =>
    reachable.get(from)?.has(to) ?? false;

  // A node lies on a cycle exactly when it can reach itself.
  const cycleNodes = nodeIds.filter(id => reaches(id, id));

  // Nodes that can reach each other belong to the same cycle cluster.
  const clusterOf = (node: string) =>
    cycleNodes.filter(other => reaches(node, other) && reaches(other, node));

  // Every member of a cluster derives the same member list, so keeping the
  // nodes that come first in their own list yields each cluster exactly once.
  const cycleClusters = cycleNodes
    .filter(node => clusterOf(node)[0] === node)
    .map(clusterOf)
    .filter(cluster => cluster.some(id => computedFieldIds.has(id)));

  // Only edges with both endpoints inside a cluster close the cycle; edges
  // merely leading into or out of it keep their normal styling.
  const cycleEdgeKeys = new Set(
    cycleClusters.flatMap(cluster => {
      const memberIds = new Set(cluster);
      return edges
        .filter(
          edge => memberIds.has(edge.source) && memberIds.has(edge.target)
        )
        .map(edge => `${edge.source}-${edge.target}`);
    })
  );

  return {
    cycleNodeIds: new Set(cycleClusters.flat()),
    cycleEdgeKeys,
    cycleCount: cycleClusters.length,
  };
};
